import { tt } from '@agos/shared'

/**
 * The restaurant's name, links and map — and which of the two places they are stored in.
 *
 * **FB-126.** Legacy's save mutates the theme context, fires the request, swallows every error and then
 * says it saved:
 *
 * ```tsx
 * setRestaurantName(localName);
 * setAdvanced(advToSave);
 * try { await fetch(`${API_BASE}/api/settings/social`, { method: "PUT", … }) } catch {}
 * toast({ title: "Nastavenia uložené", description: "…boli uložené." })
 * ```
 *
 * `GET /settings/social` is open to `main_admin` and `admin`; **`PUT` is `main_admin` only**. So an
 * `admin` can open this page, change the address and the TikTok link, press save, be told it saved —
 * and have the request answered 403 into an empty `catch`. The screen then holds values the server does
 * not, until a reload quietly puts them back.
 *
 * **FB-129.** And four fields live in *both* sections, written by two different requests from the same
 * button. `PUT /settings/social` is a `replaceSection`, so it also rewrites the whole section every
 * time.
 */

export interface SocialSettings {
  restaurantName?: string
  address?: string
  facebookUrl?: string
  instagramUrl?: string
  tiktokUrl?: string
  googleReviewsUrl?: string
  mapEmbedUrl?: string
}

export const EMPTY_SOCIAL: Required<SocialSettings> = {
  restaurantName: '',
  address: '',
  facebookUrl: '',
  instagramUrl: '',
  tiktokUrl: '',
  googleReviewsUrl: '',
  mapEmbedUrl: '',
}

export function socialOf(settings: SocialSettings | undefined): Required<SocialSettings> {
  return {
    restaurantName: settings?.restaurantName ?? '',
    address: settings?.address ?? '',
    facebookUrl: settings?.facebookUrl ?? '',
    instagramUrl: settings?.instagramUrl ?? '',
    tiktokUrl: settings?.tiktokUrl ?? '',
    googleReviewsUrl: settings?.googleReviewsUrl ?? '',
    mapEmbedUrl: settings?.mapEmbedUrl ?? '',
  }
}

/**
 * Every key the section holds, sent every time.
 *
 * `PUT /settings/social` is `replaceSection("social_settings", body)` — it does not merge. A partial
 * body silently drops whatever it omits, so the payload is the whole shape or nothing.
 */
export function socialPayload(social: Required<SocialSettings>): Required<SocialSettings> {
  return {
    restaurantName: social.restaurantName.trim(),
    address: social.address.trim(),
    facebookUrl: social.facebookUrl.trim(),
    instagramUrl: social.instagramUrl.trim(),
    tiktokUrl: social.tiktokUrl.trim(),
    googleReviewsUrl: social.googleReviewsUrl.trim(),
    mapEmbedUrl: social.mapEmbedUrl.trim(),
  }
}

/**
 * **FB-129.** The four fields both sections hold.
 *
 * `social_settings` and `design_settings` each carry `restaurantName`, `facebookUrl`, `instagramUrl`
 * and `mapEmbedUrl`, written by two different requests from one save button — the design half through
 * the theme context's debounced `PUT /settings/design`, the social half directly. If one succeeds and
 * the other does not they diverge, and nothing says which the customer site reads. They agree on this
 * data; the check exists so that the day they stop, the page notices instead of the owner.
 */
export const SHARED_WITH_DESIGN: readonly (keyof SocialSettings)[] = [
  'restaurantName',
  'facebookUrl',
  'instagramUrl',
  'mapEmbedUrl',
]

export interface Divergence {
  field: keyof SocialSettings
  social: string
  design: string
}

export function divergences(
  social: Required<SocialSettings>,
  design: Record<string, unknown>,
): Divergence[] {
  return SHARED_WITH_DESIGN.map((field) => ({
    field,
    social: social[field],
    design: typeof design[field] === 'string' ? design[field] : '',
  })).filter((entry) => entry.social.trim() !== entry.design.trim())
}

export function fieldLabel(field: keyof SocialSettings): string {
  if (field === 'restaurantName') return tt('Názov reštaurácie')
  if (field === 'facebookUrl') return 'Facebook'
  if (field === 'instagramUrl') return 'Instagram'
  if (field === 'tiktokUrl') return 'TikTok'
  if (field === 'googleReviewsUrl') return tt('Hodnotenia Google')
  if (field === 'mapEmbedUrl') return tt('Mapa')
  return tt('Adresa')
}

// ─── the URLs ────────────────────────────────────────────────────────────────

export function isValidUrl(value: string): boolean {
  const text = value.trim()
  if (text === '') return true
  try {
    const url = new URL(text)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/** The home page may be a path on the customer site or a full address. */
export function isValidHomePage(value: string): boolean {
  const text = value.trim()
  if (text === '') return true
  return text.startsWith('/') || isValidUrl(text)
}

const GOOGLE_MAPS_HOSTS: readonly string[] = [
  'www.google.com',
  'google.com',
  'maps.google.com',
  'goo.gl',
  'maps.app.goo.gl',
]

export function isGoogleMapsUrl(value: string): boolean {
  const text = value.trim()
  if (text === '') return false
  try {
    return GOOGLE_MAPS_HOSTS.includes(new URL(text).hostname)
  } catch {
    return false
  }
}

/**
 * A Google Maps link turned into one an iframe will accept.
 *
 * Somebody pastes what the *Share* button gives them, which is a navigation link. An iframe needs
 * `output=embed` or an `/embed` path, and a navigation link renders as a refusal. Legacy converts on
 * save; the conversion happens here so the field can show what will be stored.
 */
export function toEmbedUrl(raw: string): string | null {
  const text = raw.trim()
  if (text === '') return null

  let url: URL
  try {
    url = new URL(text)
  } catch {
    return null
  }
  if (!GOOGLE_MAPS_HOSTS.includes(url.hostname)) return null

  // Already embeddable.
  if (url.searchParams.get('output') === 'embed') return text
  if (url.pathname.includes('/embed')) return text

  // `@48.087,18.185,18z` in the path is the coordinate pair the share link carries.
  const coordinates = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(url.pathname)
  if (coordinates !== null) {
    return `https://maps.google.com/maps?q=${coordinates[1]}%2C${coordinates[2]}&output=embed`
  }

  const query = url.searchParams.get('q')
  if (query !== null && query !== '') {
    return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`
  }

  // A short link cannot be resolved from the browser; it is stored as it is and will not embed.
  return null
}

export function isEmbeddableMapUrl(value: string): boolean {
  const text = value.trim()
  if (text === '') return true
  return toEmbedUrl(text) !== null
}

export type UrlField = 'facebookUrl' | 'instagramUrl' | 'tiktokUrl' | 'googleReviewsUrl' | 'homePageUrl' | 'mapEmbedUrl'

export type UrlProblem = 'invalid' | 'not-embeddable' | null

export function urlProblem(field: UrlField, value: string): UrlProblem {
  if (field === 'homePageUrl') return isValidHomePage(value) ? null : 'invalid'
  if (field === 'mapEmbedUrl') {
    if (!isValidUrl(value)) return 'invalid'
    return isEmbeddableMapUrl(value) ? null : 'not-embeddable'
  }
  return isValidUrl(value) ? null : 'invalid'
}

export function urlProblemMessage(field: UrlField, problem: UrlProblem): string {
  if (problem === null) return ''
  if (field === 'homePageUrl') {
    return tt('Použite cestu na tomto webe (/uvodna-stranka) alebo celú adresu (https://...).')
  }
  if (problem === 'not-embeddable') {
    return tt(
      'Tento odkaz z Google Máp sa nedá vložiť do stránky - skrátené odkazy (goo.gl) sa vložiť nedajú. Použite odkaz s súradnicami alebo priamo vložený odkaz z Máp.',
    )
  }
  return tt('Neplatná adresa. Použite tvar https://...')
}

/** Every URL field checked at once, so a save can refuse before it writes anything. */
export function urlProblems(
  values: Partial<Record<UrlField, string>>,
): Partial<Record<UrlField, UrlProblem>> {
  const problems: Partial<Record<UrlField, UrlProblem>> = {}
  for (const [field, value] of Object.entries(values) as [UrlField, string][]) {
    const problem = urlProblem(field, value)
    if (problem !== null) problems[field] = problem
  }
  return problems
}

export function hasUrlProblem(problems: Partial<Record<UrlField, UrlProblem>>): boolean {
  return Object.keys(problems).length > 0
}

// ─── the page's own settings ─────────────────────────────────────────────────

export const LOGO_HEIGHT_MIN = 40
export const LOGO_HEIGHT_MAX = 200

export function clampLogoHeight(value: number): number {
  if (!Number.isFinite(value)) return LOGO_HEIGHT_MIN
  return Math.min(LOGO_HEIGHT_MAX, Math.max(LOGO_HEIGHT_MIN, Math.round(value)))
}

export const PAGE_TITLE_MAX = 60
export const PAGE_DESCRIPTION_MAX = 160
